import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { GetAssetReviewContentSuccessDto } from '@api/Api';
import { useAssetReviewContentQuery } from '@hooks/queries/assetReview';

interface IShareReviewContext {
  reviewId: string | null;
  password: string | null;
  passwordRequired: boolean;
  passwordDoesNotMatch: boolean;
  isLoading: boolean;
  isDisabled: boolean;
  isExpired: boolean;
  content: GetAssetReviewContentSuccessDto | undefined;
  commentMentions: boolean;
  setPassword: (password: string | null) => void;
  refetch: () => void;
}

const ShareReviewContext = createContext<IShareReviewContext>(undefined as any);

export function useShareReview<TOptional extends boolean = false>(
  optional?: TOptional
): TOptional extends true ? IShareReviewContext | null : IShareReviewContext {
  const res = useContext(ShareReviewContext);
  if (!res) {
    if (optional) return null as any;
    throw new Error('useShareReview must be used within a ShareReviewProvider');
  }
  return res as any;
}

interface ShareReviewProviderProps {
  readonly children: ReactNode;
}

export function ShareReviewProvider({ children }: ShareReviewProviderProps) {
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('reviewId');
  const isActiveParam = searchParams.get('active');

  const [password, setPasswordState] = useState<string | null>(() => {
    return localStorage.getItem(`asset-review-password-${reviewId}`);
  });

  const setPassword = useCallback(
    (newPassword: string | null) => {
      const key = `asset-review-password-${reviewId}`;
      if (newPassword) localStorage.setItem(key, newPassword);
      else localStorage.removeItem(key);
      setPasswordState(newPassword);
    },
    [reviewId]
  );

  const isDisabledByParam = isActiveParam === 'false';

  const query = useAssetReviewContentQuery({
    params: { reviewId: reviewId ?? '', password },
    enabled: !!reviewId && !isDisabledByParam
  });

  const errorCode = isAxiosError(query.error)
    ? (query.error.response?.data as any)?.errorCode
    : null;

  const passwordRequired = errorCode === 'password_does_not_match';
  const passwordDoesNotMatch = errorCode === 'password_does_not_match';
  const isExpired = query.isError && errorCode !== 'password_does_not_match';
  const isDisabled =
    isDisabledByParam ||
    (query.isError && errorCode !== 'password_does_not_match');

  const contextValue = useMemo<IShareReviewContext>(
    () => ({
      reviewId,
      password,
      passwordRequired,
      passwordDoesNotMatch,
      isLoading: query.isLoading,
      isDisabled,
      isExpired,
      content: query.data,
      commentMentions: query.data?.commentMentions ?? false,
      setPassword,
      refetch: query.refetch
    }),
    [
      reviewId,
      password,
      passwordRequired,
      passwordDoesNotMatch,
      query.isLoading,
      isDisabled,
      isExpired,
      query.data,
      setPassword,
      query.refetch
    ]
  );

  return (
    <ShareReviewContext.Provider value={contextValue}>
      {children}
    </ShareReviewContext.Provider>
  );
}
